import React, { useEffect, useRef, useState } from 'react';

import { Button } from '../../ui/button';
import type { CustomScreenComponentProps } from './types';
import onboardingContent from './onboardingContent.json';
import { Card, CardContent, CardTitle } from 'components/ui/card';
import { getTestId } from 'utils/testUtils';
import { Icon, IconName } from 'components/icons/Icon';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';

const pillarsData = onboardingContent.pillars;

const DESKTOP_PILLAR_SIZE = 112;
const DESKTOP_RADIUS = 215;
const MOBILE_PILLAR_SIZE = 76;
const MOBILE_RADIUS = 108;

export const OnboardingPillarsRevealed: React.FC<CustomScreenComponentProps> = ({
  question,
  isSubmitting,
  onComplete,
}) => {
  const { isMobile } = useResponsiveLayout();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerCenter, setContainerCenter] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const pillarSize = isMobile ? MOBILE_PILLAR_SIZE : DESKTOP_PILLAR_SIZE;
  const radius = isMobile ? MOBILE_RADIUS : DESKTOP_RADIUS;
  const canvasSize = pillarSize + radius * 2;

  useEffect(() => {
    const updateCenter = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerCenter({
          x: rect.width / 2,
          y: rect.height / 2,
        });
      }
    };

    const timer = setTimeout(updateCenter, 0);
    window.addEventListener('resize', updateCenter);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateCenter);
    };
  }, [canvasSize]);

  const getPillarPosition = (index: number) => {
    if (!containerCenter) return { top: 0, left: 0 };

    const angleStep = (2 * Math.PI) / pillarsData.length;
    const startAngle = -Math.PI / 2;
    const angle = startAngle + index * angleStep;

    const x = containerCenter.x - pillarSize / 2 + radius * Math.cos(angle);
    const y = containerCenter.y - pillarSize / 2 + radius * Math.sin(angle);

    return { top: Math.round(y), left: Math.round(x) };
  };

  return (
    <Card
      {...getTestId(`question-card-${question.id}`)}
      padding="none"
      className="w-full max-w-[62.5rem] bg-transparent"
      maxWidth="none"
    >
      <CardTitle title="10 Pillars of High Performance Living" subtitle="Click each topic to learn more" centered />
      <CardContent variant="userResponse" padding="large" className="w-full rounded-3xl">
        <div
          ref={containerRef}
          className="relative mx-auto"
          style={{ width: canvasSize, height: canvasSize }}
        >
          {containerCenter &&
            pillarsData.map((pillar, index) => (
              <Pillar
                key={pillar.id}
                icon={pillar.icon as IconName}
                label={pillar.label}
                size={pillarSize}
                position={getPillarPosition(index)}
              />
            ))}
        </div>
      </CardContent>
      <div className="p-6">
        <Button onClick={() => onComplete()} disabled={isSubmitting} type="button" variant="secondary" size="auto">
          Continue
        </Button>
      </div>
    </Card>
  );
};

interface PillarProps {
  icon: IconName;
  label: string;
  size: number;
  position: { top: number; left: number };
  onClick?: () => void;
}

const Pillar: React.FC<PillarProps> = ({
  icon,
  label,
  size,
  position,
  onClick,
}) => {
  const compact = size < 90;
  const iconSize = icon === 'fork' ? (compact ? 30 : 40) : (compact ? 24 : 32);
  return (
    <button
      className="absolute group cursor-pointer transition-transform hover:scale-105"
      style={{ top: `${position.top}px`, left: `${position.left}px`, width: `${size}px`, height: `${size}px` }}
      onClick={onClick}
      aria-label={label.replace(/\n/g, ' ')}
    >
      <div className={`flex h-full w-full flex-col items-center justify-center rounded-full bg-surface-pillars-soft/80 transition-colors group-hover:bg-surface-pillars-hover/90 ${compact ? 'gap-1 p-2 pt-2.5' : 'gap-2 p-3 pt-4'}`}>
        <Icon name={icon} size={iconSize} />
        <span
          className={`font-body-XS-semibold font-[number:var(--body-XS-semibold-font-weight)] text-center break-words text-foundation-orange-900 tracking-[var(--body-XS-semibold-letter-spacing)] [font-style:var(--body-XS-semibold-font-style)] ${compact ? 'text-[0.62rem] leading-tight' : 'text-[length:var(--body-XS-semibold-font-size)] leading-[var(--body-XS-semibold-line-height)]'}`}
        >
          {label}
        </span>
      </div>
    </button>
  );
};
